// Aba 1 · Corte — toca o cut.mp4 REAL (o que o render entregou) e edita os takes
// como o preview do edvid: trim por alça, delete alterna remoção (fantasma fica),
// duplo-clique reseta. Enquanto há ajustes pendentes a agulha anda no rascunho e o
// vídeo toca o render antigo pelo mapeamento draftToRendered.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { EdlRange, EdvidJobState } from '../../../types/videoEdit';
import {
  draftFromRanges, draftLayout, draftToRendered, draftToRenderedStrict, fmt, layoutTotal, renderedLayout,
  renderedToDraft, takeDirty, type DraftTake, SPEED_OPTIONS, fmtSpeed, speedOf,
} from './edvidTime';
import { usePlayhead, usePlayheadLoop, videoController, type MediaController } from './playhead';
import { useTimelineZoom } from './useTimelineZoom';
import { Transport } from './Transport';
import { CutTimeline } from './CutTimeline';
import { NotesPanel } from './notes';
import { BTN, BTN_PRIMARY, BusyOverlay, INPUT, LABEL, Panel, StageLayout, useKeys } from './ui';
import type { EdvidNotesApi } from './notes';
import { IconSpark, IconWarn } from './icons';

export interface CutTabProps {
  edvid: EdvidJobState;
  busy: boolean;
  notes: EdvidNotesApi;
  onSaveCut: (ranges: EdlRange[], baseRevision: number) => Promise<boolean>;
  onRecut: (note?: string) => Promise<void>;
  onApprove: () => void;
  onDirtyChange: (n: number) => void;
  onApplyNotes: () => void;
  applyingNotes: boolean;
  toast: (msg: string) => void;
  /** botão Publicar ou agendar (secundário aqui: dá para publicar o corte limpo sem visual) */
  publish?: React.ReactNode;
}

export function CutTab({ edvid, busy, notes, onSaveCut, onRecut, onApprove, onDirtyChange, onApplyNotes, applyingNotes, toast: flash, publish }: CutTabProps) {
  const cut = edvid.cut!;
  const fps = cut.fps || cut.edl.fps || 30;
  const jcut = cut.edl.jcut_timeline;
  const rendered = cut.edl.ranges;
  const [draft, setDraft] = useState<DraftTake[]>(() => draftFromRanges(rendered));
  const [selected, setSelectedOne] = useState(-1);
  // seleção múltipla (Ctrl/Cmd/Shift no clique da lista) — a velocidade vale para todos os marcados
  const [picked, setPicked] = useState<Set<number>>(() => new Set());
  const setSelected = useCallback((i: number) => { setSelectedOne(i); setPicked(i >= 0 ? new Set([i]) : new Set()); }, []);
  const togglePick = useCallback((i: number, range: boolean) => {
    setSelectedOne(i);
    setPicked(prev => {
      const next = new Set(prev);
      if (range && prev.size) {
        const anchor = [...prev].pop()!;
        const [a, b] = anchor < i ? [anchor, i] : [i, anchor];
        for (let k = a; k <= b; k++) next.add(k);
      } else if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }, []);
  const [portrait, setPortrait] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recutOpen, setRecutOpen] = useState(false);
  const [recutNote, setRecutNote] = useState('');
  const [controller, setController] = useState<MediaController | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const playhead = usePlayhead();
  const revRef = useRef(cut.revision);
  // o próximo corte do servidor é o render dos NOSSOS ajustes: adota sem perguntar
  const adoptNext = useRef(false);

  // o <video> remonta quando o layout troca (vertical ↔ horizontal): o controle segue o elemento vivo
  useEffect(() => {
    if (videoRef.current) setController(videoController(videoRef.current));
  }, [portrait]);

  const dirtyN = draft.filter(takeDirty).length;
  useEffect(() => { onDirtyChange(dirtyN); }, [dirtyN, onDirtyChange]);

  // corte novo do servidor: adota se não há ajuste pendente; senão avisa (poll do edvid)
  useEffect(() => {
    if (revRef.current === cut.revision) return;
    revRef.current = cut.revision;
    const adopt = adoptNext.current;
    adoptNext.current = false;
    setDraft(prev => {
      if (!adopt && prev.some(takeDirty)) {
        toast('Novo corte disponível — salve ou descarte seus ajustes para atualizar.', { duration: 4000 });
        return prev;
      }
      return draftFromRanges(rendered);
    });
    setSelected(-1);
  }, [cut.revision, rendered, setSelected]);

  const dl = useMemo(() => draftLayout(draft, jcut, fps), [draft, jcut, fps]);
  const rl = useMemo(() => renderedLayout(rendered, jcut), [rendered, jcut]);
  const total = Math.max(layoutTotal(dl), 0.1);
  const timelineTotal = Math.max(total, dirtyN ? 0 : cut.durationSec);
  const toTimeline = useCallback((t: number) => renderedToDraft(t, dl, rl), [dl, rl]);
  const toMedia = useCallback((t: number) => draftToRendered(t, dl, rl, cut.durationSec), [dl, rl, cut.durationSec]);
  const toMediaStrict = useCallback((t: number) => draftToRenderedStrict(t, dl, rl), [dl, rl]);

  usePlayheadLoop(controller, toTimeline, playhead);
  const zoom = useTimelineZoom(panelRef, timelineTotal, () => playhead.get());

  const seekDraft = useCallback((t: number) => {
    const tt = Math.max(0, Math.min(t, total));
    controller?.seek(toMedia(tt));
    playhead.set(tt);
  }, [controller, toMedia, total, playhead]);

  const patchTake = useCallback((i: number, patch: Partial<DraftTake>) => {
    setDraft(prev => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  }, []);
  const resetTake = useCallback((i: number) => {
    setDraft(prev => prev.map((r, k) => (k === i ? { ...r, start: r.orig.start, end: r.orig.end, speed: r.orig.speed > 1 ? r.orig.speed : undefined, removed: false } : r)));
  }, []);

  const mark = useCallback(() => {
    const msg = notes.toggleMark(playhead.get(), 'cut', toMedia);
    if (msg) flash(msg);
  }, [notes, playhead, toMedia, flash]);

  useKeys(useCallback((e: KeyboardEvent) => {
    if (e.key === 'm' || e.key === 'M') { e.preventDefault(); mark(); return; }
    if (e.key === 'Escape' && notes.pendingIn != null) { notes.setPendingIn(null); flash('IN cancelado'); return; }
    if (e.code === 'Space') {
      e.preventDefault();
      if (!controller) return;
      if (controller.isPaused()) controller.play(); else controller.pause();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const step = e.shiftKey ? 1 : 1 / fps;
      seekDraft(playhead.get() + (e.key === 'ArrowRight' ? step : -step));
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && (picked.size || selected >= 0) && !busy) {
      e.preventDefault();
      const targets = picked.size ? picked : new Set([selected]);
      setDraft(prev => prev.map((r, k) => (targets.has(k) ? { ...r, removed: !r.removed } : r)));
    }
  }, [mark, notes, flash, controller, fps, seekDraft, playhead, selected, picked, busy]));

  const pickedList = [...picked].filter(i => draft[i] && !draft[i].removed);
  const pickedSpeeds = new Set(pickedList.map(i => speedOf(draft[i])));
  const currentSpeed = pickedSpeeds.size === 1 ? [...pickedSpeeds][0] : null;
  const applySpeed = (v: number) => {
    const targets = new Set(pickedList);
    setDraft(prev => prev.map((r, k) => (targets.has(k) ? { ...r, speed: v > 1 ? v : undefined } : r)));
  };

  const save = async () => {
    const ranges: EdlRange[] = draft.filter(r => !r.removed).map(({ removed: _r, orig: _o, ...r }) => ({
      ...r, start: +r.start.toFixed(3), end: +r.end.toFixed(3), speed: speedOf(r) > 1 ? speedOf(r) : undefined,
    }));
    if (!ranges.length) { flash('Não dá para salvar um corte sem nenhum take.'); return; }
    setSaving(true);
    adoptNext.current = true;
    const ok = await onSaveCut(ranges, cut.revision);
    if (!ok) adoptNext.current = false;
    setSaving(false);
    if (ok) setSelected(-1);
  };

  const sel = selected >= 0 ? draft[selected] : null;

  const player = (
    <>
      <video
        ref={videoRef}
        src={cut.url}
        playsInline
        preload="auto"
        className="h-full w-full bg-black object-contain"
        onLoadedMetadata={e => {
          const v = e.currentTarget;
          if (v.videoWidth && v.videoHeight) setPortrait(v.videoHeight > v.videoWidth);
        }}
        onClick={() => { if (!controller) return; if (controller.isPaused()) controller.play(); else controller.pause(); }}
      />
      {edvid.phase === 'cutting' && <BusyOverlay title="Renderizando o corte…" message={edvid.message} />}
    </>
  );

  return (
    <StageLayout portrait={portrait} player={player}>
      <Transport
        controller={controller}
        playhead={playhead}
        total={total}
        markArmed={notes.pendingIn != null}
        onMark={mark}
        slider={zoom.slider}
        onSlider={zoom.setSlider}
        onFit={zoom.fit}
      >
        {dirtyN > 0 && (
          <div className="ml-2 flex items-center gap-2">
            <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-black text-amber-300">{dirtyN} {dirtyN === 1 ? 'ajuste' : 'ajustes'}</span>
            <button type="button" className={BTN} onClick={() => { setDraft(draftFromRanges(rendered)); setSelected(-1); }} disabled={saving}>Descartar</button>
            <button type="button" className={BTN_PRIMARY} onClick={() => void save()} disabled={saving || busy}>{saving ? 'Salvando…' : 'Salvar'}</button>
          </div>
        )}
      </Transport>

      <CutTimeline
        cut={cut}
        draft={draft}
        dl={dl}
        rl={rl}
        total={timelineTotal}
        editable={!busy}
        selected={selected}
        panelRef={panelRef}
        pps={zoom.pps}
        playhead={playhead}
        onSeek={seekDraft}
        toMedia={toMediaStrict}
        notes={notes.all.filter(n => n.phase === 'cut')}
        pendingIn={notes.pendingIn}
        onOpenNote={id => notes.setEditing({ id, isNew: false })}
        onSelect={setSelected}
        onDraft={patchTake}
        onReset={resetTake}
        version={draft}
      />

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-3">
          <Panel title="Estratégia do corte">
            <p className="text-xs leading-relaxed text-slate-200">{cut.strategy || '—'}</p>
            {cut.verifySummary && (
              <p className={`mt-2 rounded-xl px-2.5 py-1.5 font-mono text-[10.5px] leading-relaxed ${cut.verifyFlags ? 'bg-amber-500/10 text-amber-200' : 'bg-emerald-500/10 text-emerald-200'}`}>
                {cut.verifySummary}
              </p>
            )}
            {cut.warnings?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {cut.warnings.map((w, i) => (
                  <li key={i} className="flex gap-1.5 text-[11px] text-amber-200"><IconWarn width={12} height={12} className="mt-0.5 shrink-0" />{w}</li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Próximo passo">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={`${BTN} flex items-center gap-1.5`} onClick={() => setRecutOpen(v => !v)} disabled={busy}>
                <IconSpark width={12} height={12} /> Refazer corte pela IA
              </button>
              {publish}
              <button
                type="button"
                className={`${BTN_PRIMARY} ml-auto`}
                disabled={busy || dirtyN > 0}
                title={dirtyN > 0 ? 'Salve ou descarte os ajustes antes de aprovar' : undefined}
                onClick={onApprove}
              >
                Aprovar corte → Estilo
              </button>
            </div>
            {recutOpen && (
              <div className="mt-3 space-y-2">
                <textarea
                  value={recutNote}
                  onChange={e => setRecutNote(e.target.value)}
                  rows={2}
                  placeholder="O que a IA deve fazer diferente? (opcional) Ex.: “comece pelo resultado”, “tire a parte da nota fiscal”."
                  className={`${INPUT} resize-none text-xs`}
                />
                <div className="flex justify-end gap-2">
                  <button type="button" className={BTN} onClick={() => setRecutOpen(false)}>Cancelar</button>
                  <button
                    type="button"
                    className={BTN_PRIMARY}
                    onClick={async () => { await onRecut(recutNote.trim() || undefined); setRecutOpen(false); setRecutNote(''); }}
                  >
                    Refazer corte
                  </button>
                </div>
              </div>
            )}
          </Panel>

          <NotesPanel
            notes={notes.all}
            phase="cut"
            isLocal={notes.isLocal}
            onOpen={id => notes.setEditing({ id, isNew: false })}
            onSeek={seekDraft}
            onApply={onApplyNotes}
            applying={applyingNotes}
          />
        </div>

        <Panel title={`Takes (${draft.filter(r => !r.removed).length}/${draft.length})`} right={sel && <span className="font-mono text-[10px] text-slate-400">{fmt(sel.start)} → {fmt(sel.end)}</span>}>
          <div className="mb-2 rounded-xl border border-white/10 bg-white/[0.02] p-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-black text-slate-200">Velocidade</span>
              <span className="text-[10.5px] text-slate-500">
                {pickedList.length ? `${pickedList.length} ${pickedList.length === 1 ? 'trecho selecionado' : 'trechos selecionados'}` : 'selecione trechos na lista (Ctrl/Shift para vários)'}
              </span>
              <button
                type="button"
                className="ml-auto text-[10.5px] font-bold text-indigo-300 hover:text-indigo-200 disabled:opacity-40"
                disabled={busy}
                onClick={() => { setPicked(new Set(draft.map((r, i) => (r.removed ? -1 : i)).filter(i => i >= 0))); }}
              >
                Selecionar todos
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Velocidade dos trechos selecionados">
              {SPEED_OPTIONS.map(v => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={currentSpeed === v}
                  disabled={busy || !pickedList.length}
                  onClick={() => applySpeed(v)}
                  className={`h-7 min-w-11 rounded-lg border px-2 font-mono text-[11px] font-black transition-colors disabled:opacity-40 ${currentSpeed === v ? 'border-indigo-400 bg-indigo-500/20 text-white' : 'border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200'}`}
                >
                  {fmtSpeed(v)}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-slate-500">Vídeo e voz aceleram juntos, sem alterar o tom da voz. Salve para renderizar e depois aprove.</p>
          </div>
          <ol className="max-h-[340px] space-y-1 overflow-y-auto pr-1">
            {dl.map((r, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={e => {
                    if (e.ctrlKey || e.metaKey || e.shiftKey) { togglePick(i, e.shiftKey); return; }
                    setSelected(i);
                    seekDraft(r.out + 0.01);
                  }}
                  className={`w-full rounded-xl border px-2.5 py-2 text-left transition-colors ${picked.has(i) ? 'border-indigo-400/60 bg-indigo-500/10' : 'border-transparent hover:bg-white/5'} ${r.removed ? 'opacity-45' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-slate-500">{String(i + 1).padStart(2, '0')}</span>
                    <span className={`text-[10px] font-black uppercase tracking-wider ${r.removed ? 'text-slate-500 line-through' : 'text-indigo-300'}`}>{r.beat || r.source}</span>
                    {takeDirty(r) && !r.removed && <span className="rounded bg-amber-500/15 px-1 text-[9px] font-black text-amber-300">editado</span>}
                    {r.removed && <span className="rounded bg-white/10 px-1 text-[9px] font-black text-slate-400">removido</span>}
                    {!r.removed && speedOf(r) !== 1 && <span className="rounded bg-indigo-500/20 px-1 font-mono text-[9px] font-black text-indigo-200">{fmtSpeed(speedOf(r))}</span>}
                    <span className="ml-auto font-mono text-[10px] text-slate-500">{r.removed ? '—' : `${r.dur.toFixed(2)}s`}</span>
                  </div>
                  {r.quote && <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-300">“{r.quote}”</p>}
                  {r.reason && <p className="mt-0.5 text-[10px] italic text-slate-500">{r.reason}</p>}
                </button>
              </li>
            ))}
          </ol>
          <p className={`${LABEL} mt-2 !normal-case !tracking-normal !font-semibold`}>Clique seleciona · Ctrl/Shift+clique seleciona vários · delete remove · duplo-clique no take reseta.</p>
        </Panel>
      </div>
    </StageLayout>
  );
}
